import React, {PureComponent} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import IssueFieldInlineEdit from './IssueFieldInlineEdit';
import InputValidationView from '@adsk/bim360-matrix-react-input-validation-view';
import InlineEditTextField from '@adsk/bim360-matrix-react-inline-edit-text-field';
import TextFieldCore from '@adsk/bim360-matrix-react-text-field-core';
import {issueAttributesType} from '../../constants/proptype-validation';
import {presenceValidator} from '../../utils/validation-helpers';
import i18n from '../../utils/i18n';
import {trim} from 'lodash';

class TextFieldInlineEdit extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      initialInputValue: '',
      inputValue: '',
      isEditing: false
    };
  }

  handleInlineEditEnd(value, isCancel, updateIssue, closeEditMode) {
    if (!isCancel && this.isValid() && this.isChanged(value)) {
      updateIssue(trim(value));
    } else {
      closeEditMode();
    }
  }

  isValid() {
    const validRequired = !this.props.isRequired || presenceValidator(this.state.inputValue);
    return (this.props.inputValidator ? !this.props.inputValidator(this.state.inputValue) : true) && validRequired;
  }

  isChanged(value) {
    return value !== this.state.initialInputValue;
  }

  getErrorMessage() {
    const {inputValue, isEditing} = this.state;
    // Required validation
    if (isEditing && this.props.isRequired && !presenceValidator(inputValue)) {
      return i18n.t('new.fieldRequired');
    }
    const inputValidationMessage = this.props.inputValidator && this.props.inputValidator(inputValue);
    if (inputValidationMessage) {
      return inputValidationMessage;
    }
    return '';
  }

  /**
   * render
   * @returns {XML}
   */
  render() {
    const {
      issue,
      attributeName,
      className,
      label,
      placeholder,
      styleType,
      linkify,
      disabled,
      multiline,
      getFieldValue,
      getIssueAttributes
    } = this.props;

    const classes = classNames('TextFieldInlineEdit TextFieldInlineEdit__input-validation', className);
    const errorMessage = this.getErrorMessage();
    const isValid = !(this.state.isEditing && errorMessage);
    return (
      <InputValidationView message={errorMessage} className={classes} popoverContainer={this}>
        <IssueFieldInlineEdit
          issue={issue}
          className="TextFieldInlineEdit__field"
          data-testid={this.props['data-testid']}
          attributeName={attributeName}
          label={label}
          placeholder={placeholder}
          styleType={styleType}
          linkify={linkify}
          disabled={disabled}
          getFieldValue={getFieldValue}
          getIssueAttributes={getIssueAttributes}
        >
          {(currentValue, closeEditMode, updateIssue) => (
            <InlineEditTextField
              value={currentValue}
              valid={isValid}
              autoselect
              behavior={multiline ? TextFieldCore.Behaviors.MULTI_LINE : TextFieldCore.Behaviors.SINGLE_LINE}
              onInlineEditStart={inputValue =>
                this.setState({
                  initialInputValue: inputValue,
                  inputValue,
                  isEditing: true
                })}
              onChange={inputValue => this.setState({inputValue})}
              onInlineEditEnd={(value, isCancel) => {
                this.setState({
                  inputValue: '',
                  isEditing: false
                });
                this.handleInlineEditEnd(value, isCancel, updateIssue, closeEditMode);
              }}
            />
          )}
        </IssueFieldInlineEdit>
      </InputValidationView>
    );
  }
}

TextFieldInlineEdit.displayName = 'TextFieldInlineEdit';

TextFieldInlineEdit.defaultProps = {
  multiline: true
};

TextFieldInlineEdit.propTypes = {
  issue: issueAttributesType.isRequired,
  attributeName: PropTypes.oneOf([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  className: PropTypes.string,
  'data-testid': PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  isRequired: PropTypes.bool,
  styleType: PropTypes.oneOf(Object.values(IssueFieldInlineEdit.StyleTypes)),
  linkify: PropTypes.bool,
  inputValidator: PropTypes.func,
  getFieldValue: PropTypes.func,
  getIssueAttributes: PropTypes.func,
  multiline: PropTypes.bool,
  disabled: PropTypes.bool
};

export default TextFieldInlineEdit;
